import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireActiveAuth as requireSupabaseAuth } from "@/lib/auth/requireActiveAuth";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { productRequiresAgreement } from "@/lib/store.agreement";

const SiteOrigin = () =>
  process.env.SITE_URL ||
  process.env.PUBLIC_SITE_URL ||
  "https://currencythoughts.net";

function assertStripeReady() {
  if (!process.env.STRIPE_SECRET_KEY) {
    throw new Error(
      "Stripe is not configured yet. Add STRIPE_SECRET_KEY in Lovable Cloud secrets to enable checkout.",
    );
  }
}

async function getStripe() {
  assertStripeReady();
  const Stripe = (await import("stripe")).default;
  return new Stripe(process.env.STRIPE_SECRET_KEY!, {
    // Pin a recent API version; Stripe SDK keeps types in sync.
    apiVersion: "2024-06-20" as never,
  });
}

// ============ Public catalog ============

export const listProducts = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data, error } = await supabaseAdmin
      .from("products")
      .select(
        "id, slug, title, subtitle, description, price_cents, product_type, category, thumbnail_url, featured, pages, duration_minutes, order_index",
      )
      .eq("published", true)
      .order("featured", { ascending: false })
      .order("order_index", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  },
);

export const getProductBySlug = createServerFn({ method: "GET" })
  .inputValidator((i) => z.object({ slug: z.string().min(1).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const { data: product, error } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!product) return null;
    // Never leak product_files here.
    return product;
  });

export const getProductPageData = createServerFn({ method: "GET" })
  .inputValidator((i) => z.object({ slug: z.string().min(1).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const { data: product, error } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!product) return null;

    const [{ data: files }, { data: related }] = await Promise.all([
      supabaseAdmin
        .from("product_files")
        .select("id, file_name, mime_type, file_size_bytes, order_index")
        .eq("product_id", product.id)
        .order("order_index", { ascending: true }),
      supabaseAdmin
        .from("products")
        .select(
          "id, slug, title, subtitle, price_cents, product_type, category, thumbnail_url, pages, duration_minutes",
        )
        .eq("category", product.category)
        .eq("published", true)
        .neq("id", product.id)
        .order("featured", { ascending: false })
        .limit(3),
    ]);

    return {
      product,
      files: files ?? [],
      related: related ?? [],
    };
  });

// ============ Checkout ============

const CheckoutInput = z.object({
  productIds: z.array(z.string().uuid()).min(1).max(20),
});

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => CheckoutInput.parse(i))
  .handler(async ({ data, context }) => {
    assertStripeReady();

    // An account is REQUIRED before payment can be captured: the order must
    // always have an entitlement target. There is no guest path.
    const userId = context.userId;
    if (!userId) throw new Error("Sign in before checking out.");

    const { data: products, error } = await supabaseAdmin
      .from("products")
      .select(
        "id, slug, title, price_cents, thumbnail_url",
      )
      .in("id", data.productIds)
      .eq("published", true);
    if (error) throw new Error(error.message);
    if (!products || products.length === 0) {
      throw new Error("No purchasable products found.");
    }

    const userEmail =
      (context.claims?.email as string | undefined) ?? undefined;

    const subtotal = products.reduce((s, p) => s + p.price_cents, 0);

    const { data: order, error: orderErr } = await supabaseAdmin
      .from("orders")
      .insert({
        user_id: userId,
        guest_email: null,
        subtotal_cents: subtotal,
        total_cents: subtotal,
        status: "pending",
        fulfillment_status: "pending_pma",
      })
      .select("*")
      .single();
    if (orderErr) throw new Error(orderErr.message);

    const itemsRows = products.map((p) => ({
      order_id: order.id,
      product_id: p.id,
      product_title_snapshot: p.title,
      price_cents_snapshot: p.price_cents,
    }));
    const { error: itemsErr } = await supabaseAdmin
      .from("order_items")
      .insert(itemsRows);
    if (itemsErr) throw new Error(itemsErr.message);

    const stripe = await getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      customer_email: userEmail,
      line_items: products.map((p) => ({
        quantity: 1,
        price_data: {
          currency: "usd",
          unit_amount: p.price_cents,
          product_data: {
            name: p.title,
            images: p.thumbnail_url ? [p.thumbnail_url] : undefined,
          },
        },
      })),
      success_url: `${SiteOrigin()}/store/checkout/success?order_id=${order.id}&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${SiteOrigin()}/store/checkout/cancel?order_id=${order.id}`,
      metadata: {
        order_id: order.id,
        user_id: userId,
      },
    });

    await supabaseAdmin
      .from("orders")
      .update({ stripe_session_id: session.id })
      .eq("id", order.id);

    return { url: session.url, orderId: order.id };
  });

// ============ Order retrieval ============

export const getOrderForCurrentBuyer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ orderId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: order, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id, status, fulfillment_status, total_cents, paid_at, fulfillment_due_at, delivered_at, user_id, guest_email",
      )
      .eq("id", data.orderId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!order) return null;
    if (order.user_id !== context.userId) throw new Error("Order not found.");

    const { data: items } = await supabaseAdmin
      .from("order_items")
      .select("id, product_id, product_title_snapshot, price_cents_snapshot")
      .eq("order_id", order.id);

    const { data: agreement } = await supabaseAdmin
      .from("order_agreements")
      .select("id, signed_at")
      .eq("order_id", order.id)
      .maybeSingle();

    const productIds = (items ?? []).map((i) => i.product_id);
    let agreementRequired = false;
    if (productIds.length > 0) {
      const { data: prods } = await supabaseAdmin
        .from("products")
        .select("id, product_type")
        .in("id", productIds);
      agreementRequired = (prods ?? []).some((p) =>
        productRequiresAgreement(p.product_type),
      );
    }

    return {
      order,
      items: items ?? [],
      agreementSigned: !!agreement,
      agreementRequired,
    };
  });

// ============ Agreement signing (gates delivery) ============

const AgreementInput = z.object({
  orderId: z.string().uuid(),
  signedName: z.string().min(2).max(200),
  signedEmail: z.string().email(),
  acknowledged: z.literal(true),
});

export const signOrderAgreement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => AgreementInput.parse(i))
  .handler(async ({ data, context }) => {
    const { data: order, error } = await supabaseAdmin
      .from("orders")
      .select("id, status, user_id, guest_email")
      .eq("id", data.orderId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!order) throw new Error("Order not found.");
    if (order.user_id !== context.userId) throw new Error("Order not found.");
    if (order.status !== "paid") {
      throw new Error("Order is not yet paid.");
    }

    const ip = getRequestIP({ xForwardedFor: true }) ?? null;
    const ua = getRequestHeader("user-agent") ?? null;

    const { error: agErr } = await supabaseAdmin
      .from("order_agreements")
      .upsert(
        {
          order_id: order.id,
          pma_version: "v1",
          no_refund_acknowledged: true,
          signed_name: data.signedName,
          signed_email: data.signedEmail,
          signed_ip: ip,
          user_agent: ua,
          signed_at: new Date().toISOString(),
        },
        { onConflict: "order_id" },
      );
    if (agErr) throw new Error(agErr.message);

    await supabaseAdmin
      .from("orders")
      .update({ fulfillment_status: "pma_signed" })
      .eq("id", order.id);

    await supabaseAdmin.from("activity_logs").insert({
      user_id: order.user_id,
      action: "order.agreement_signed",
      target_type: "order",
      target_id: order.id,
    });

    return { ok: true };
  });

// ============ Signed download URL ============

export const signProductDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        productId: z.string().uuid(),
        fileId: z.string().uuid().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;

    // Ownership check
    const { data: lib } = await supabaseAdmin
      .from("user_library")
      .select("id, order_id, revoked_at")
      .eq("user_id", userId)
      .eq("product_id", data.productId)
      .maybeSingle();
    if (!lib) throw new Error("You do not own this product.");
    if (lib.revoked_at) {
      throw new Error(
        "Access to this resource was revoked after a refund or payment dispute.",
      );
    }

    // Agreement check — only for product types that actually require the PMA.
    // Publishing products (workbooks, ebooks, guides) download immediately.
    const { data: prod } = await supabaseAdmin
      .from("products")
      .select("product_type")
      .eq("id", data.productId)
      .maybeSingle();
    if (productRequiresAgreement(prod?.product_type)) {
      const { data: agreement } = await supabaseAdmin
        .from("order_agreements")
        .select("signed_at")
        .eq("order_id", lib.order_id ?? "")
        .maybeSingle();
      if (!agreement?.signed_at) {
        throw new Error("Sign the member agreement before downloading.");
      }
    }

    // Resolve file
    const fileQuery = supabaseAdmin
      .from("product_files")
      .select("id, storage_path, file_name")
      .eq("product_id", data.productId)
      .order("order_index", { ascending: true });
    const { data: files } = await fileQuery;
    if (!files || files.length === 0) {
      throw new Error("No downloadable files attached to this product yet.");
    }
    const file = data.fileId
      ? files.find((f) => f.id === data.fileId) ?? files[0]
      : files[0];

    const { data: signed, error: signErr } = await supabaseAdmin.storage
      .from("product-files")
      .createSignedUrl(file.storage_path, 60 * 60, {
        download: file.file_name,
      });
    if (signErr || !signed?.signedUrl) {
      throw new Error(signErr?.message ?? "Failed to sign download URL");
    }

    const { data: current } = await supabaseAdmin
      .from("user_library")
      .select("download_count")
      .eq("id", lib.id)
      .maybeSingle();
    await supabaseAdmin
      .from("user_library")
      .update({
        last_downloaded_at: new Date().toISOString(),
        download_count: (current?.download_count ?? 0) + 1,
      })
      .eq("id", lib.id);

    await supabaseAdmin.from("activity_logs").insert({
      user_id: userId,
      action: "library.download",
      target_type: "product",
      target_id: data.productId,
    });

    return { url: signed.signedUrl, fileName: file.file_name };
  });

// ============ Library list (authenticated) ============

export const listMyLibrary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await supabaseAdmin
      .from("user_library")
      .select(
        "id, granted_at, last_downloaded_at, download_count, revoked_at, revoked_reason, product:products(id, slug, title, subtitle, thumbnail_url, product_type)",
      )
      .eq("user_id", context.userId)
      .order("granted_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// ============ Admin: awaiting fulfillment ============

export const adminListPendingFulfillment = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await supabaseAdmin.rpc("is_admin", {
      _user_id: context.userId,
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { data, error } = await supabaseAdmin
      .from("orders")
      .select(
        "id, total_cents, status, fulfillment_status, fulfillment_due_at, paid_at, user_id, guest_email",
      )
      .eq("status", "paid")
      .neq("fulfillment_status", "delivered")
      .order("fulfillment_due_at", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const adminMarkDelivered = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ orderId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await supabaseAdmin.rpc("is_admin", {
      _user_id: context.userId,
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { error } = await supabaseAdmin
      .from("orders")
      .update({
        fulfillment_status: "delivered",
        delivered_at: new Date().toISOString(),
      })
      .eq("id", data.orderId);
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("activity_logs").insert({
      user_id: context.userId,
      action: "order.delivered",
      target_type: "order",
      target_id: data.orderId,
    });
    return { ok: true };
  });